import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { api } from "../lib/api";
import BlogBlock from "../components/BlogBlock";
import PageSEO from "../components/PageSEO";
import StructuredData, { blogPostingLD, breadcrumbLD } from "../components/StructuredData";
import { track, useScrollCompletion } from "../lib/analytics";

const SITE_URL = "https://artistryperspectives.com";
const DEFAULT_OG = `${SITE_URL}/og-image.jpg`;

/* ============================================================
   Public blog routes:
   /blog          → index (BlogIndex)
   /blog/:slug    → article (BlogArticle)
   ============================================================ */

export function BlogIndex({ settings }) {
  const [blogs, setBlogs] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeTag, setActiveTag] = useState("");

  useEffect(() => {
    document.title = "Notes — Artistry Perspectives";
    window.scrollTo(0, 0);
    api.get("/blogs?status=published").then(({ data }) => setBlogs(data || []));
    api.get("/blogs/tags").then(({ data }) => setTags(data || []));
  }, []);

  const filtered = useMemo(
    () => (activeTag ? blogs.filter((b) => (b.tags || []).includes(activeTag)) : blogs),
    [blogs, activeTag],
  );
  const featured = filtered.find((b) => b.featured) || filtered[0];
  const rest = featured ? filtered.filter((b) => b.slug !== featured.slug) : [];

  return (
    <main data-testid="blog-index" className="bg-[#0A0A0C] min-h-screen">
      <PageSEO
        title="Notes — Artistry Perspectives"
        description="Long-form thinking on positioning, voice, distribution, and the small things that turn an audience into a brand."
        image={DEFAULT_OG}
        url={`${SITE_URL}/blog`}
      />
      <div className="px-6 md:px-12 lg:px-20 pt-36 md:pt-44 pb-24 md:pb-32 max-w-[1600px] mx-auto">
        <p className="overline text-[#C9A050]/70 mb-8">Notes</p>
        <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[7rem] tracking-[-0.04em] leading-[0.95] text-white">
          On building <span className="font-serif-accent text-[#C9A050]">brands</span> that last.
        </h1>
        <p className="mt-10 md:mt-14 text-[#A1A1AA] text-lg md:text-xl max-w-2xl leading-relaxed">
          Long-form thinking on positioning, voice, distribution, and the small things that turn an audience into a brand.
        </p>

        {/* Tag filter — respects admin master toggle + admin-hidden tags list */}
        {(() => {
          if (settings?.show_blog_filter === false) return null;
          const hidden = new Set((settings?.hidden_blog_tags || []).map((t) => t.toLowerCase()));
          const visibleTags = tags.filter((t) => !hidden.has(t.toLowerCase()));
          if (visibleTags.length === 0) return null;
          return (
            <div data-testid="blog-tags" className="flex flex-wrap gap-3 mt-14 md:mt-20">
              <button
                onClick={() => setActiveTag("")}
                data-testid="blog-tag-all"
                className={`overline px-4 py-2 border transition-colors ${activeTag === "" ? "border-[#C9A050] text-[#C9A050]" : "border-white/15 text-[#8A8A93] hover:text-white"}`}
              >
                All
              </button>
              {visibleTags.map((t) => (
                <button
                  key={t}
                  data-testid={`blog-tag-${t.toLowerCase()}`}
                  onClick={() => setActiveTag(t)}
                  className={`overline px-4 py-2 border transition-colors ${activeTag === t ? "border-[#C9A050] text-[#C9A050]" : "border-white/15 text-[#8A8A93] hover:text-white"}`}
                >
                  {t}
                </button>
              ))}
            </div>
          );
        })()}

        {filtered.length === 0 && (
          <p className="text-[#8A8A93] text-lg mt-20" data-testid="blog-empty">
            No published articles yet — check back soon.
          </p>
        )}

        {/* Featured + grid */}
        {featured && (
          <div className="mt-16 md:mt-24">
            <FeaturedCard blog={featured} />
          </div>
        )}
        {rest.length > 0 && (
          <div data-testid="blog-grid" className="mt-20 md:mt-28 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-14">
            {rest.map((b) => (
              <BlogCard key={b.slug} blog={b} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

function FeaturedCard({ blog }) {
  return (
    <Link
      to={`/blog/${blog.slug}`}
      data-testid="blog-featured"
      className="group grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center"
    >
      {blog.cover_image && (
        <div className="aspect-[4/3] md:aspect-[5/4] overflow-hidden bg-[#15151A]">
          <img
            src={blog.cover_image}
            alt={blog.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
          />
        </div>
      )}
      <div>
        <p className="overline text-[#C9A050]/70 mb-5">Featured · {blog.kicker || (blog.tags || [])[0] || "Article"}</p>
        <h2 className="font-display text-3xl md:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.05] text-white group-hover:text-[#C9A050] transition-colors">
          {blog.title}
        </h2>
        {blog.subtitle && <p className="mt-6 text-[#A1A1AA] text-lg md:text-xl leading-relaxed whitespace-pre-line">{blog.subtitle}</p>}
        <p className="mt-8 overline text-[#8A8A93]">
          {blog.read_time_min ? `${blog.read_time_min} min read` : "Read"} →
        </p>
      </div>
    </Link>
  );
}

function BlogCard({ blog }) {
  return (
    <Link to={`/blog/${blog.slug}`} className="group block" data-testid={`blog-card-${blog.slug}`}>
      {blog.cover_image && (
        <div className="aspect-[4/3] overflow-hidden bg-[#15151A] mb-6">
          <img
            src={blog.cover_image}
            alt={blog.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          />
        </div>
      )}
      {blog.kicker && <p className="overline text-[#C9A050]/70 mb-3">{blog.kicker}</p>}
      <h3 className="font-display text-2xl md:text-3xl tracking-tight leading-tight text-white group-hover:text-[#C9A050] transition-colors">
        {blog.title}
      </h3>
      {blog.excerpt && <p className="mt-4 text-[#A1A1AA] text-base leading-relaxed">{blog.excerpt}</p>}
      <p className="mt-6 overline text-[#8A8A93]">{blog.read_time_min ? `${blog.read_time_min} min read` : "Read"} →</p>
    </Link>
  );
}

/* ============================================================
   /blog/:slug — article
   ============================================================ */

export default function BlogArticle() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);
  const { scrollYProgress } = useScroll();
  const articleRef = useRef(null);

  useEffect(() => {
    setError(false);
    setBlog(null);
    api.get(`/blogs/${slug}`)
      .then(({ data }) => {
        setBlog(data);
        document.title = (data?.meta_title || data?.title || "Notes") + " — Artistry Perspectives";
        track("blog_read_started", {
          slug: data?.slug || slug,
          title: data?.title,
          tags: data?.tags || [],
        });
      })
      .catch(() => setError(true));
    window.scrollTo(0, 0);
  }, [slug]);

  useScrollCompletion(articleRef, 0.8, () => {
    track("blog_read_completed", {
      slug: blog?.slug || slug,
      title: blog?.title,
    });
  });

  // Pull related once we have the post
  useEffect(() => {
    if (!blog) return;
    api.get("/blogs?status=published").then(({ data }) => {
      const others = (data || []).filter((b) => b.slug !== blog.slug);
      const sameTag = others.filter((b) => (b.tags || []).some((t) => (blog.tags || []).includes(t)));
      const pick = (sameTag.length >= 3 ? sameTag : [...sameTag, ...others.filter((o) => !sameTag.includes(o))]).slice(0, 3);
      setRelated(pick);
    });
  }, [blog]);

  if (error) {
    return (
      <main className="min-h-[80vh] flex flex-col items-center justify-center px-6 text-center">
        <p className="overline mb-4">Notes</p>
        <h1 className="font-display text-4xl md:text-6xl tracking-tighter mb-8">Article not found.</h1>
        <Link
          to="/blog"
          data-testid="blog-back"
          className="overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-6 py-3 transition-all"
        >
          ← All notes
        </Link>
      </main>
    );
  }

  if (!blog) return <main className="min-h-screen bg-[#0A0A0C]" />;

  const meta = {
    title: blog.meta_title || blog.title,
    description: blog.meta_description || blog.excerpt || blog.subtitle || "",
    og: blog.og_image || blog.cover_image || "",
  };

  return (
    <main ref={articleRef} data-testid={`blog-${slug}`} className="bg-[#0A0A0C] min-h-screen">
      <PageSEO
        title={`${meta.title} — Artistry Perspectives`}
        description={meta.description}
        image={meta.og || DEFAULT_OG}
        url={`${SITE_URL}/blog/${slug}`}
        type="article"
      />
      {/* JSON-LD for AEO — BlogPosting + breadcrumb trail */}
      <StructuredData id={`blog-${slug}`} data={blogPostingLD(blog)} />
      <StructuredData
        id={`blog-${slug}-crumb`}
        data={breadcrumbLD([
          { name: "Home", item: "/" },
          { name: "Blog", item: "/blog" },
          { name: blog.title, item: `/blog/${slug}` },
        ])}
      />
      {/* Document metadata via simple side-effect */}
      <BlogMeta meta={meta} />

      {/* Scroll-progress bar */}
      <motion.div
        data-testid="blog-scroll-progress"
        className="fixed top-0 left-0 right-0 h-px bg-[#C9A050] origin-left z-[60] pointer-events-none"
        style={{ scaleX: scrollYProgress }}
      />

      {/* HERO */}
      <header className="px-6 md:px-12 lg:px-20 pt-36 md:pt-44 pb-16 md:pb-20 max-w-[1600px] mx-auto">
        <Link to="/blog" className="overline text-[#8A8A93] hover:text-[#C9A050] transition-colors">
          ← All notes
        </Link>
        <p data-testid="blog-kicker" className="overline text-[#C9A050]/70 mt-12 mb-6">
          {blog.kicker || (blog.tags || []).join(" · ") || "Article"}
        </p>
        <h1 className="font-display text-4xl sm:text-5xl md:text-7xl lg:text-[7rem] leading-[0.95] tracking-[-0.04em] text-white max-w-[18ch]">
          {blog.title}
          <span className="text-[#C9A050]">.</span>
        </h1>
        {blog.subtitle && (
          <p className="mt-10 md:mt-14 font-serif-accent italic text-[#E4E4E7] text-2xl md:text-3xl leading-[1.4] max-w-3xl whitespace-pre-line">
            {blog.subtitle}
          </p>
        )}
        <div className="mt-14 md:mt-20 flex items-center gap-6 flex-wrap text-[#8A8A93]">
          <span className="overline">{blog.author_name || "Artistry Perspectives"}</span>
          {blog.read_time_min && <span className="text-xs">·</span>}
          {blog.read_time_min && <span className="overline">{blog.read_time_min} min read</span>}
          {blog.published_at && <><span className="text-xs">·</span><span className="overline">{new Date(blog.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span></>}
        </div>
      </header>

      {/* COVER */}
      {blog.cover_image && (
        <div data-testid="blog-cover" className="px-0 md:px-12 lg:px-20 mb-20 md:mb-28 max-w-[1600px] mx-auto">
          <div className="aspect-[16/9] md:aspect-[21/9] overflow-hidden bg-[#15151A]">
            <img
              src={blog.cover_image}
              alt={blog.title}
              className="w-full h-full object-cover"
              style={{ objectPosition: blog.cover_focal || "center" }}
            />
          </div>
        </div>
      )}

      {/* BODY blocks */}
      <article className="px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto">
        <div data-testid="blog-body" className="space-y-10 md:space-y-14 max-w-3xl">
          {(blog.blocks || []).length === 0 && (
            <p className="text-[#8A8A93] italic">This article is being written. Check back soon.</p>
          )}
          {(blog.blocks || []).map((b, i) => (
            <BlogBlock key={i} block={b} />
          ))}
        </div>
      </article>

      {/* AUTHOR + CTA */}
      <section className="px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto mt-24 md:mt-32 pt-12 md:pt-16 border-t border-white/5">
        <div className="max-w-3xl">
          <p className="overline text-[#C9A050]/70 mb-3">Written by</p>
          <p className="font-display text-2xl md:text-3xl text-white">{blog.author_name || "Artistry Perspectives"}</p>
          <p className="mt-3 text-[#A1A1AA] italic font-serif-accent text-lg md:text-xl whitespace-pre-line">
            {blog.author_signature || "We build brands designed to last, not trend."}
          </p>
        </div>

        <div className="max-w-3xl mt-16 md:mt-24">
          <p className="font-serif-accent italic text-[#E4E4E7] text-2xl md:text-3xl lg:text-4xl leading-[1.4] mb-10">
            If this thinking resonates, we should work together.
          </p>
          <div className="flex items-center gap-5 flex-wrap">
            <Link
              to="/contact"
              data-testid="blog-cta-primary"
              className="overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-7 py-4 transition-all"
            >
              Start a project →
            </Link>
            <Link
              to="/work"
              data-testid="blog-cta-secondary"
              className="overline border border-white/15 text-white hover:border-[#C9A050] hover:text-[#C9A050] px-7 py-4 transition-all"
            >
              View our work
            </Link>
          </div>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section data-testid="blog-related" className="px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto mt-28 md:mt-40 pb-24 md:pb-32">
          <p className="overline text-[#C9A050]/70 mb-12">Continue reading</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
            {related.map((r) => (
              <BlogCard key={r.slug} blog={r} />
            ))}
          </div>
        </section>
      )}

      {/* DISCLAIMER */}
      <div className="px-6 md:px-12 lg:px-20 pb-20 md:pb-24 max-w-4xl mx-auto">
        <div className="border-t border-[#C9A050]/15 pt-10">
          <p className="text-center text-xs md:text-sm text-[#8A8A93] tracking-wide leading-relaxed font-serif-accent italic">
            © Artistry Perspectives 2026. Property of Artistry Perspectives Studio. Shared for the perspective of brand owners and as an educational reference — open viewing access for students.
          </p>
        </div>
      </div>
    </main>
  );
}

/* Document head meta updater (no extra dependency) */
function BlogMeta({ meta }) {
  useEffect(() => {
    const set = (name, content, attr = "name") => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    set("description", meta.description);
    set("og:title", meta.title, "property");
    set("og:description", meta.description, "property");
    set("og:image", meta.og, "property");
    set("twitter:card", "summary_large_image");
    set("twitter:title", meta.title);
    set("twitter:description", meta.description);
    if (meta.og) set("twitter:image", meta.og);
  }, [meta]);
  return null;
}
